import type { ComponentType } from "react";

import { MapIcon, Share2Icon, WandSparklesIcon } from "lucide-react";

import {
  FacebookIcon,
  GoogleIcon,
  type BrandIconProps,
} from "simplydo/react/ui/brand-icon";
import { IconData } from "simplydo/react/ui/icon";

import CompaniesHouseIcon from "src/images/companiesHouse-icon.png";
import CreditSafeIcon from "src/images/creditsafe-icon.png";
import CrunchbaseIcon from "src/images/crunchbase-icon.png";
import DuckDuckGoIcon from "src/images/duckduckgo-icon.ico";
import HetasIcon from "src/images/hetas-icon.png";
import HunterIOIcon from "src/images/hunterio-icon.ico";
import SerpApiIcon from "src/images/serpapi-icon.ico";
import ThomsonLocalIcon from "src/images/thomsonlocal-icon.ico";

type BrandIconComponent = ComponentType<BrandIconProps>;

const revenueMap = {
  r_00000000: "Less than $1M",
  r_00001000: "$1M to $10M",
  r_00010000: "$10M to $50M",
  r_00050000: "$50M to $100M",
  r_00100000: "$100M to $500M",
  r_00500000: "$500M to $1B",
  r_01000000: "$1B to $10B",
  r_10000000: "$10B+",
};

const employeeCountMap = {
  c_00001_00010: "1-10",
  c_00011_00050: "11-50",
  c_00051_00100: "51-100",
  c_00101_00250: "101-250",
  c_00251_00500: "251-500",
  c_00501_01000: "501-1000",
  c_01001_05000: "1001-5000",
  c_05001_10000: "5001-10000",
  c_10001_max: "10001+",
};

const crunchbaseImageUrl =
  "https://res.cloudinary.com/crunchbase-production/image/upload/";

const companyStatuses = [
  { value: "active", text: "Active" },
  { value: "dissolved", text: "Dissolved" },
  { value: "liquidation", text: "Liquidated" },
  { value: "receivership", text: "Receivership" },
  { value: "administration", text: "Under administration" },
  { value: "voluntary-arrangement", text: "Voluntary arrangement" },
  { value: "converted-closed", text: "Converted closed" },
  { value: "insolvency-proceedings", text: "Insolvency proceedings" },
  { value: "registered", text: "Registered" },
  { value: "removed", text: "Removed" },
];

const sourceInfo: Record<
  string,
  {
    name: string;
    url?: string;
    favicon?: string;
    icon?: IconData;
    BrandIcon?: BrandIconComponent;
  }
> = {
  crunchbase: {
    name: "Crunchbase",
    url: "https://www.crunchbase.com/",
    favicon: CrunchbaseIcon,
  },
  creditsafe: {
    name: "CreditSafe",
    url: "https://www.creditsafe.com/",
    favicon: CreditSafeIcon,
  },
  creditsafeReport: {
    name: "CreditSafe Report",
    url: "https://www.creditsafe.com/",
    favicon: CreditSafeIcon,
  },
  companiesHouse: {
    name: "Companies House",
    url: "https://www.gov.uk/government/organisations/companies-house",
    favicon: CompaniesHouseIcon,
  },
  google: {
    name: "Google",
    url: "https://www.google.com/",
    BrandIcon: GoogleIcon,
  },
  googleMaps: {
    name: "Google Maps",
    url: "https://www.google.com/maps/",
    icon: MapIcon,
  },
  facebook: {
    name: "Facebook",
    url: "https://www.facebook.com/",
    BrandIcon: FacebookIcon,
  },
  facebookScrape: {
    name: "Facebook Profile",
    url: "https://www.facebook.com/",
    BrandIcon: FacebookIcon,
  },
  serpapi: {
    name: "Serp API",
    url: "https://serpapi.com/",
    favicon: SerpApiIcon,
  },
  hunterioLookup: {
    name: "Hunter.io",
    url: "https://hunter.io/",
    favicon: HunterIOIcon,
  },
  duckduckgo: {
    name: "DuckDuckGo",
    url: "https://duckduckgo.com/",
    favicon: DuckDuckGoIcon,
  },
  thomsonlocal: {
    name: "Thomson Local",
    url: "https://www.thomsonlocal.com/",
    favicon: ThomsonLocalIcon,
  },
  hetas: { name: "HETAS", url: "https://www.hetas.co.uk/", favicon: HetasIcon },
  aiSearch: { name: "AI Search", icon: WandSparklesIcon },
  foursquare: {
    name: "Foursquare",
    url: "https://foursquare.com/",
    icon: MapIcon,
  },
  ocrScan: { name: "OCR Scan" },
  patents: { name: "Patents View" },
  twitter: { name: "𝕏", url: "https://x.com/", icon: Share2Icon },
  websiteScrape: { name: "Company Website" },
  website: { name: "Company Website" },
  blogTask: { name: "RSS Feed" },
  geocoding: { name: "Geolocation API" },
};

export {
  revenueMap,
  employeeCountMap,
  crunchbaseImageUrl,
  companyStatuses,
  sourceInfo,
};
